/**
 * QuestionsWithSections
 *
 * Top-level V2 editor for Custom Surveys. Reads sections from form state
 * (`surveyForm.surveyParts`) via `useSectionsFormArray`, renders one
 * `<SectionCard />` per section inside a drag-reorderable list, and exposes
 * a shared bottom action bar with `+ Add Question` (V2-only, targets the
 * currently expanded section) and `+ Add Section`.
 *
 * Parent `CustomSurvey/index.tsx` decides V1 vs V2 rendering based
 * on whether any part is a `SurveySection`. This component assumes V2 shape.
 */
import { css } from 'aphrodite';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button } from '../../../../../components-v2/Button';
import { SectionsActionBar } from '../SectionsActionBar';
import { ReorderSectionsModalComponent } from './components/ReorderSectionsModal/ReorderSectionsModal';
import { SectionQuestionsReorderModalComponent } from './components/SectionQuestionsReorderModal/SectionQuestionsReorderModal';
import { useSectionsFormArray } from './hooks/useSectionsFormArray';
import { SectionCard } from './SectionCard';
import { styleSheet } from './styles';
import type { SurveyPart, SurveyQuestion, SurveySection } from './types';
import { isSurveySection } from './types';
import { reorderParts, reorderQuestions } from './utils/reorderCommit';
import { createBlankQuestion } from './utils/transform';
import { applyVisibilityCleanup } from './utils/validation';

// Drag-and-drop is intentionally NOT used in the inline V2 editor: react-beautiful-dnd
// v8.0.3 crashes under React 18 StrictMode (uses deprecated childContextTypes; double-
// mount breaks its internal state machine). Section reorder is handled via the section
// …menu's "Reorder" action, which opens a modal (matches V1's `ReorderQuestionsModal`
// pattern). The designer confirmed modal reorder is a common UX pattern.

const FIELD_PREFIX = 'surveyForm.surveyParts';

export interface QuestionsWithSectionsProps {
	/** Whether the parent survey is being edited vs. created for the first time. */
	isEdit: boolean;
	/** Whether the reorder buttons are unlocked (editing a saved survey, or template mode). */
	canReorder: boolean;
	/** Portal target rendered at formContainer level in SurveyTabs; reorder buttons teleport here. */
	reorderSlot?: HTMLElement | null;
}

export const QuestionsWithSections: React.FC<QuestionsWithSectionsProps> = ({ isEdit, canReorder, reorderSlot }) => {
	const { control, getValues, setValue } = useFormContext();
	// `moveSection` is intentionally not destructured — section reorder is deferred to a
	// modal (V1's `ReorderQuestionsModal` pattern) instead of inline drag-and-drop.
	const { sections, appendEmptySection, removeSection, duplicateSectionAt, reorderSections } = useSectionsFormArray();

	// Live-watched survey parts — reflects sub-path setValue updates (e.g. a question
	// changing fieldType / _type via the picker) that `useFieldArray`'s `fields` snapshot
	// does NOT see. This is what visibility eligibility filters
	// (SectionTriggers, QuestionVisibilityControls) must consume to stay accurate.
	const liveSurveyParts =
		(useWatch({ control, name: FIELD_PREFIX }) as SurveyPart[] | undefined) ?? (sections as unknown as SurveyPart[]);

	// Track which section is currently expanded; default to the first section.
	const [expandedSectionId, setExpandedSectionId] = React.useState<string | null>(
		sections.length > 0 ? sections[0].id : null
	);

	// Derived state from saved parts and expanded section
	// Index into surveyParts (the path used for question/section reads & writes). Derive from
	// liveSurveyParts — which may interleave flat (non-section) parts — so it stays correct
	// rather than the sections-only field-array snapshot.
	const expandedIdx = liveSurveyParts.findIndex(p => isSurveySection(p) && p.id === expandedSectionId);
	const liveSections = liveSurveyParts.filter(isSurveySection) as SurveySection[];
	const expandedSection = expandedIdx >= 0 ? (liveSurveyParts[expandedIdx] as SurveyPart | undefined) : undefined;
	const expandedQuestions = (
		expandedSection && isSurveySection(expandedSection) ? expandedSection.questions : []
	) as SurveyQuestion[];
	const expandedSectionTitle = expandedSection && isSurveySection(expandedSection) ? expandedSection.title : undefined;
	const [showReorderQuestionsModal, setShowReorderQuestionsModal] = React.useState(false);
	const [showReorderSectionsModal, setShowReorderSectionsModal] = React.useState(false);

	// Keep the expanded section valid: recover when none is selected but sections exist,
	// and fall back to the first when the current expanded section is removed/reordered.
	React.useEffect(() => {
		const liveSectionList = ((getValues(FIELD_PREFIX) as SurveyPart[] | undefined) ?? []).filter(isSurveySection);
		if (!expandedSectionId) {
			if (liveSectionList.length > 0) {
				setExpandedSectionId(liveSectionList[0].id);
			}
			return;
		}
		const stillExists = liveSectionList.some(s => s.id === expandedSectionId);
		if (!stillExists) {
			setExpandedSectionId(liveSectionList.length > 0 ? liveSectionList[0].id : null);
		}
	}, [expandedSectionId, liveSurveyParts, sections, getValues]);

	const handleExpand = React.useCallback((sectionId: string) => {
		setExpandedSectionId(sectionId);
	}, []);

	const handleAddSection = React.useCallback(() => {
		// Append at the end of liveSurveyParts (appendEmptySection delegates to
		// useFieldArray.append, which always appends). Use liveSurveyParts.length as
		// the read-back index so it is accurate even if flat (non-section) parts are
		// ever interleaved before the sections-only snapshot catches up.
		const beforePartsLen = liveSurveyParts.length;
		appendEmptySection();
		// The hook updates synchronously for `append` — read fresh values to grab the new id.
		const fresh = getValues(FIELD_PREFIX) as { id?: string }[] | undefined;
		const newSection = fresh?.[beforePartsLen];
		if (newSection?.id) setExpandedSectionId(newSection.id);
	}, [liveSurveyParts.length, appendEmptySection, getValues]);

	const handleAddQuestion = React.useCallback(() => {
		// Decide which section receives the new question.
		// Spec §4.4: "adds to the *currently expanded* section. If no section is
		// expanded, expand the last section and add there."
		// Resolve targetIndex against liveSurveyParts (the full parts array) so that
		// the path written to surveyParts.${targetIndex}.questions is correct even
		// when flat (non-section) parts are interleaved before the sections-only snapshot.
		let targetIndex = liveSurveyParts.findIndex(p => isSurveySection(p) && p.id === expandedSectionId);
		if (targetIndex < 0) {
			// Find the last section in liveSurveyParts for the fallback.
			let lastSectionIndex = -1;
			let lastSectionId: string | null = null;
			for (let i = liveSurveyParts.length - 1; i >= 0; i--) {
				const p = liveSurveyParts[i];
				if (isSurveySection(p)) {
					lastSectionIndex = i;
					lastSectionId = p.id;
					break;
				}
			}
			if (lastSectionIndex < 0) return; // no sections — bottom-bar shouldn't be hit here
			targetIndex = lastSectionIndex;
			setExpandedSectionId(lastSectionId);
		}

		// Append a blank question to that section's questions array.
		// Shape matches V1 `FieldList.tsx:71-72` (id, label, fieldType, isHidden, isOptional).
		const questionsPath = `${FIELD_PREFIX}.${targetIndex}.questions`;
		const current = (getValues(questionsPath) ?? []) as unknown[];
		setValue(questionsPath, [...current, createBlankQuestion()], { shouldDirty: true });
	}, [liveSurveyParts, expandedSectionId, getValues, setValue]);

	const handleQuestionsReorderClose = React.useCallback(
		(result?: { reorderedQuestionIds?: string[] }, cancel?: boolean) => {
			setShowReorderQuestionsModal(false);
			if (cancel || !result?.reorderedQuestionIds || expandedIdx < 0) return;
			const path = `${FIELD_PREFIX}.${expandedIdx}.questions`;
			const liveQ = (getValues(path) ?? []) as SurveyQuestion[];
			setValue(path, reorderQuestions(liveQ, result.reorderedQuestionIds), { shouldDirty: true });
			applyVisibilityCleanup(getValues, setValue);
		},
		[expandedIdx, getValues, setValue]
	);

	const handleSectionsReorderClose = React.useCallback(
		(result?: { reorderedSectionIds?: string[] }, cancel?: boolean) => {
			setShowReorderSectionsModal(false);
			if (cancel || !result?.reorderedSectionIds) return;
			const liveParts = (getValues(FIELD_PREFIX) ?? []) as SurveyPart[];
			reorderSections(reorderParts(liveParts, result.reorderedSectionIds) as SurveySection[]);
		},
		[getValues, reorderSections]
	);

	return (
		<div className={css(styleSheet.container)}>
			{reorderSlot &&
				canReorder &&
				(expandedQuestions.length > 1 || liveSections.length > 1) &&
				createPortal(
					<div className={css(styleSheet.reorderButtons)}>
						{expandedQuestions.length > 1 && (
							<Button type='button' variant='secondary' onClick={() => setShowReorderQuestionsModal(true)}>
								Reorder Questions
							</Button>
						)}
						{liveSections.length > 1 && (
							<Button type='button' variant='secondary' onClick={() => setShowReorderSectionsModal(true)}>
								Reorder Sections
							</Button>
						)}
					</div>,
					reorderSlot
				)}
			<div className={css(styleSheet.sectionList)}>
				{/* Live watch, not the useFieldArray snapshot, which lags setValue hydration. */}
				{liveSurveyParts.map((part, index) =>
					isSurveySection(part) ? (
						<SectionCard
							key={part.id}
							section={part}
							index={index}
							fieldNamePrefix={`${FIELD_PREFIX}.${index}`}
							isExpanded={part.id === expandedSectionId}
							onExpand={() => handleExpand(part.id)}
							onDuplicate={() => duplicateSectionAt(index)}
							onDelete={() => removeSection(index)}
							canDelete={sections.length > 1}
							isEdit={isEdit}
							allSurveyParts={liveSurveyParts}
						/>
					) : null
				)}
			</div>
			<div className={css(styleSheet.fixedBottomBar)}>
				<SectionsActionBar onAddSection={handleAddSection} onAddQuestion={handleAddQuestion} />
			</div>
			<SectionQuestionsReorderModalComponent
				modalProps={{ isOpen: showReorderQuestionsModal, onRequestClose: handleQuestionsReorderClose }}
				questions={(getValues(`${FIELD_PREFIX}.${expandedIdx}.questions`) ?? []) as SurveyQuestion[]}
				sectionTitle={expandedSectionTitle}
			/>
			<ReorderSectionsModalComponent
				modalProps={{ isOpen: showReorderSectionsModal, onRequestClose: handleSectionsReorderClose }}
				sections={liveSections}
			/>
		</div>
	);
};
